<script setup lang="ts">
import { Field, ErrorMessage } from 'vee-validate'
import { UserPlusIcon } from '@heroicons/vue/20/solid'
import ChatGptModelSelect from '@/components/projects/settings/ai/ChatGptModelSelect.vue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useTranslations } from '@/composables/useTranslations.ts'
import { GetProjectChatGPTAssistantInfoAndModelsAndFiles } from '@/components/projects/graphql/project.queries.ts'
import { useLazyQuery, useMutation, useQuery } from '@vue/apollo-composable'
import type {
  ChatGptAssistantFile, ChatGptAssistantForm,
  ChatGptAssistantInfo,
  ChatGptModel,
} from '@/components/projects/interfaces.ts'
import { cloneDeep, isEqual, merge, omit, round, sortBy } from 'lodash'
import ChatGptAssistantFiles from '@/components/projects/settings/assistant/ChatGptAssistantFiles.vue'
import type { RangeSliderMinMaxStep } from '@/components/interfaces/project-settings-view.interfaces.ts'
import { BackendErrorCode, ProjectAIType, ProjectSettingsField } from '@/enums.ts'
import { useToast } from 'vue-toastification'
import {
  ProjectSettingsCreateChatGptAssistant,
  ProjectSettingsUpdateChatGptAssistant,
} from '@/components/projects/graphql/project.mutations.ts'
import { isNumeric } from '@/utils/util.ts'

const props = defineProps<{
  projectId: number | null
}>()

const toast = useToast()
const { t } = useTranslations()
const isLoading = ref<boolean>(true)
const isAssistantCreated = ref(false)
const assistantKey = ref<string | null>(null)
const files = ref<ChatGptAssistantFile[]>([])
const chatGptModels = ref<ChatGptModel[]>([])
const formSettings = ref<{
  temperature: RangeSliderMinMaxStep
  topP: RangeSliderMinMaxStep
}>({
  temperature: { min: 0.1, max: 2, step: 0.01 },
  topP: { min: 0.1, max: 1, step: 0.05 },
})
const form = ref<ChatGptAssistantForm>(getFormDefaultState())
const formInitial = ref<ChatGptAssistantForm>(getFormDefaultState())
const hasChanges = computed<boolean>(() => {
  return !isEqual(form.value, formInitial.value)
})


// Variables
const variablesProjectId = ref<{ projectId: number }>( {
  projectId: props.projectId as number
})
// Queries & mutations
const {
  onError: onAssistantInfoLoadingFailed,
  onResult: onAssistantInfoReceived,
  refetch: refetchAssistantInfoModelsAndFiles
} = useQuery<{
  assistantInfo: ChatGptAssistantInfo
  chatGptModels: ChatGptModel[]
  files: ChatGptAssistantFile[]
}>(GetProjectChatGPTAssistantInfoAndModelsAndFiles, variablesProjectId, { fetchPolicy: 'cache-and-network' })
const {
  onError: onAssistantCreationFailed,
  onDone: onAssistantCreated,
  mutate: createAssistant
} = useMutation<{
  assistantInfo: ChatGptAssistantInfo
}>(ProjectSettingsCreateChatGptAssistant, { fetchPolicy: 'no-cache' })
const {
  onError: onAssistantUpdateFailed,
  onDone: onAssistantUpdateSucceed,
  mutate: updateAssistant
} = useMutation<{
  assistantInfo: ChatGptAssistantInfo
}>(ProjectSettingsUpdateChatGptAssistant, { fetchPolicy: 'no-cache' })

onAssistantInfoReceived(({ data, loading }) => {
  if (!loading) {
    const { assistantInfo, chatGptModels, files } = data

    setAssistantFormData(cloneDeep(assistantInfo))
    setAssistantInitialFormData(cloneDeep(assistantInfo))
    setChatGptModels(sortBy(cloneDeep(chatGptModels), 'ID'))
    setFiles(files)
    setAssistantCreated(true)
    setIsLoading(false)
  }
})

onAssistantInfoLoadingFailed((error) => {
  setIsLoading(false)

  if (error.message !== BackendErrorCode.ErrChatGptAssistantNotCreated) {
    toast.error(error.message)
    console.error('onAssistantInfoLoadingFailed | Error', error)
  } else {
    setFiles([])
    setAssistantCreated(false)
    setAssistantFormData(getFormDefaultState())
    setAssistantInitialFormData(getFormDefaultState())
  }
})

onAssistantCreated(async (result) => {
  console.log('onAssistantCreated | result:', result)
  toast.success('Assistant created!')
  refetchAssistantInfoModelsAndFiles()
})

onAssistantCreationFailed((error) => {
  setIsLoading(false)
  toast.error(error.message)
})

onAssistantUpdateSucceed((result) => {
  const { assistantInfo } = result.data as { assistantInfo: ChatGptAssistantInfo }
  setAssistantFormData(assistantInfo)
  toast.success('ChatGPT assistant updated!')
  setIsLoading(false)
})

onAssistantUpdateFailed((error) => {
  setIsLoading(false)
  toast.error(error.message)
  console.error('onSaveAssistantButtonClicked | onError:', error)
})

function getFormDefaultState (): ChatGptAssistantForm {
  return {
    name: '',
    model: '',
    instructions: '',
    temperature: 0.7,
    topP: 1.0,
  }
}

function setAssistantCreated(flag: boolean): void {
  isAssistantCreated.value = flag
}

function setAssistantFormData(data: ChatGptAssistantForm): void {
  form.value = data
}

function setAssistantInitialFormData(data: ChatGptAssistantForm): void {
  formInitial.value = data
}


function setFiles(data: ChatGptAssistantFile[]) {
  files.value = data
}

function setChatGptModels(models: ChatGptModel[]): void {
  chatGptModels.value = models
}

function setIsLoading(flag: boolean): void {
  isLoading.value = flag
}

async function onSaveAssistantButtonClicked(ev: Event): Promise<void> {
  ev.preventDefault()
  setIsLoading(true)

  const newForm = merge(form.value, {
    topP: round(form.value.topP, 2),
    temperature: round(form.value.temperature, 2),
  }) as unknown as ChatGptAssistantForm

  await updateAssistant({ projectId: props.projectId as number, form: newForm })

  formInitial.value = newForm
}

async function onAddAssistantButtonClicked() {
  setIsLoading(true)
  await createAssistant({ projectId: props.projectId })
}

onMounted(async () => {
  const { stop: stopProjectIdPropWatcher } = watch(() => props.projectId, async (newValue) => {
    if (isNumeric(newValue)) {
      variablesProjectId.value.projectId = newValue as number
    }

  }, { immediate: true })

  onUnmounted(() => {
    stopProjectIdPropWatcher()
  })
})
</script>

<template>
  <div class="container mx-auto">
    <div v-if="isLoading" class="w-full text-center">
      <img src="/images/loading_spinner.gif" class="w-64 h-64 m-auto" />
    </div>
    <div v-else>
      <div v-if="isAssistantCreated" class="space-y-10">
        <!-- Name and Model Section -->
        <div class="border-b border-gray-300 pb-4">
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-6">
            <!-- Name Field -->
            <div class="sm:col-span-3">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('settings.ai.assistant.name') }}
              </label>
              <div>
                <Field :name="ProjectSettingsField.AiChatGptAssistantName" v-model="form.name" v-slot="{ field, meta }">
                  <input
                    type="text"
                    v-bind="field"
                    class="block w-full rounded-lg border px-3 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    :class="{ 'border-red-500': meta.touched && !meta.valid }"
                  />
                </Field>
              </div>
              <ErrorMessage :name="ProjectSettingsField.AiChatGptAssistantName" class="mt-1 text-sm text-red-600" />
            </div>
            <!-- Model Field -->
            <div class="sm:col-span-3">
              <Field :name="ProjectSettingsField.AiChatGptAssistantModel">
                <ChatGptModelSelect v-model="form.model" :models="chatGptModels" />
                <hr />
              </Field>
            </div>
          </div>
        </div>
        <!-- System Instructions -->
        <div class="border-b border-gray-300 pb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('settings.ai.assistant.systemInstructions') }}
          </label>
          <div>
            <Field :name="ProjectSettingsField.AiChatGptAssistantInstructions" v-model="form.instructions" v-slot="{ field, meta }">
              <textarea
                rows="15"
                v-bind="field"
                class="block w-full rounded-lg border px-3 py-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                :class="{ 'border-red-500': meta.touched && !meta.valid }"
              ></textarea>
            </Field>
          </div>
          <ErrorMessage :name="ProjectSettingsField.AiChatGptAssistantInstructions" class="mt-1 text-sm text-red-600" />
        </div>

        <!-- Tools Section -->
        <div class="border-b border-gray-300">
          <ChatGptAssistantFiles
            :projectId="projectId"
            :files="files"
          />
          <!--<div v-if="files.length" class="mt-4">
            <h3 class="text-sm font-medium text-gray-700 mb-2">
              {{ t('settings.ai.assistant.uploadedFiles') }}:
            </h3>
            <ul class="list-disc pl-5">
              <li v-for="(file, index) in files" :key="index" class="text-sm text-gray-600">
                {{ file }}
              </li>
            </ul>
          </div>-->
        </div>

        <!-- Model Configuration -->
        <div class="border-b border-gray-300 pb-4">
          <h2 class="text-lg font-semibold text-gray-600 text-center">
            {{ t('settings.ai.assistant.modelConfiguration') }}
          </h2>
          <div class="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label class="block text-sm/6 font-medium text-gray-900">
                {{ t('settings.ai.assistant.temperature') }}
                <span
                  class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10"
                >
                  {{ form.temperature }}
                </span>
              </label>
              <Field
                :name="ProjectSettingsField.AiChatGptAssistantTemperature"
                type="range"
                v-model="form.temperature"
                :min="formSettings.temperature.min"
                :max="formSettings.temperature.max"
                :step="formSettings.temperature.step"
                class="w-full mt-2"
              />
              <ErrorMessage :name="ProjectSettingsField.AiChatGptAssistantTemperature" class="text-sm text-red-500" />
            </div>
            <div>
              <label class="block text-sm/6 font-medium text-gray-900">
                {{ t('settings.ai.assistant.topP') }}
                <span
                  class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10"
                >
                  {{ form.topP }}
                </span>
              </label>
              <Field
                :name="ProjectSettingsField.AiaChatGptAssistantTopP"
                type="range"
                v-model="form.topP"
                :min="formSettings.topP.min"
                :max="formSettings.topP.max"
                :step="formSettings.topP.step"
                class="w-full mt-2"
              />
              <ErrorMessage :name="ProjectSettingsField.AiaChatGptAssistantTopP" class="text-sm text-red-500" />
            </div>
          </div>
        </div>
        <div class="pb-4 text-center">
          <button
            @click="(ev) => onSaveAssistantButtonClicked(ev)"
            :disabled="!hasChanges"
            type="submit"
            class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-gray-400 disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            {{ t('settings.ai.assistant.saveAssistant') }}
          </button>
        </div>

        <!-- API Version -->
        <!--<div>
          <h2 class="text-lg font-semibold text-gray-600">
            {{ t('settings.ai.assistant.apiVersion') }}
          </h2>
          <div class="mt-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('settings.ai.assistant.latest') }}
            </label>
            <div class="text-sm text-gray-500">
              {{ t('settings.ai.assistant.updated') }} 2/17, 2:33 AM
            </div>
          </div>
        </div>-->
      </div>
      <div v-else class="w-full text-center">
        <button
          @click="onAddAssistantButtonClicked"
          type="button"
          class="inline-flex items-center gap-x-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-indigo-500 focus:ring-2 focus:ring-indigo-400 focus:outline-none w"
        >
          <UserPlusIcon class="size-5" aria-hidden="true" />
          {{ t('settings.ai.assistant.addAssistant') }}
        </button>
      </div>
    </div>
  </div>
</template>
